/**
 * AGC: Daily Quests Component for Origami Battle 2.0 - Version 6.8.5
 * AGC: Simple design matching Profile.tsx - no cards, line dividers, compact
 * AGC: NO BONUSES - Only individual quest rewards
 * @author AGC (AI-Generated Code) with Cursor
 */

import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useAccount } from 'wagmi'
import { QuestCard } from './QuestCard'
import { buildBackendUrl } from '../../utils/backendUrl'
import { Loader2, Calendar, Trophy, CheckCheck, Coins, Clock } from 'lucide-react'
import { useModal } from '../../hooks/useModal'
import { useFeatureFlags } from '../../hooks/useFeatureFlags'
import { useMiniKit } from '@coinbase/onchainkit/minikit'
import { playSoundSafe } from '../../utils/audio'

interface Quest {
  id: string
  name: string
  description?: string
  type?: string
  difficulty: 'EASY' | 'MEDIUM' | 'HARD' | 'LEGENDARY'
  requiredCount: number
  rewardOGB: number
  progress?: number
  completed?: boolean
  rewardsClaimed?: boolean
}

export const DailyQuests: React.FC = () => {
  const { address, isConnected } = useAccount()
  const [quests, setQuests] = useState<Quest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // AGC: Batch operation states
  const [verifyingAll, setVerifyingAll] = useState(false)
  const [claimingAll, setClaimingAll] = useState(false)
  const [batchProgress, setBatchProgress] = useState<string>('')
  
  // AGC: Daily quest reset countdown (resets at midnight UTC)
  const [timeUntilReset, setTimeUntilReset] = useState<string>('')

  // AGC: Modal hook for Base App compatible alerts/confirms
  const { alert, confirm, Modals } = useModal()
  
  // AGC: FIX 2025-11-29 - Get Base App username from MiniKit context for social quests
  const { isBaseAppContext } = useFeatureFlags()
  const miniKit = isBaseAppContext ? useMiniKit() : null
  const baseappUsername = useMemo(() => {
    if (!isBaseAppContext || !miniKit?.context?.user) return null
    return miniKit.context.user.username || null
  }, [isBaseAppContext, miniKit?.context?.user])

  const backendUrl = buildBackendUrl('')

  // AGC: Calculate stats
  const completedCount = quests.filter(q => q.completed).length
  const claimedCount = quests.filter(q => q.rewardsClaimed).length
  const totalRewards = quests.reduce((sum, q) => sum + (q.rewardsClaimed ? q.rewardOGB : 0), 0)
  const pendingRewards = quests
    .filter(q => q.completed && !q.rewardsClaimed)
    .reduce((sum, q) => sum + q.rewardOGB, 0)

  useEffect(() => {
    if (!isConnected || !address) return

    const loadQuests = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await fetch(`${backendUrl}/api/quests/daily/${address}`)
        if (response.ok) {
          const data = await response.json()
          setQuests(data.quests || [])
        } else {
          const errorData = await response.json().catch(() => ({}))
          if (errorData.error?.includes('INSUFFICIENT_STAKE')) {
            setError('You need to stake at least 500 OGB to access quests.')
          } else {
            setError(errorData.error || 'Failed to load quests')
          }
          setQuests([])
        }
      } catch (err) {
        console.error('❌ Error loading daily quests:', err)
        setError('Failed to connect to server')
        setQuests([])
      } finally {
        setLoading(false)
      }
    }

    loadQuests()
  }, [address, isConnected, backendUrl])

  const handleVerifyQuest = async (questId: string) => {
    if (!address) return

    // AGC: FIX 2025-11-29 - Pass baseappUsername for social quest verification
    const response = await fetch(`${backendUrl}/api/quests/verify/${address}/${questId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        category: 'DAILY',
        baseappUsername: baseappUsername || undefined // AGC: Pass username from MiniKit context
      })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to verify quest')
    }

    // AGC: Reload quests to update UI
    const questsResponse = await fetch(`${backendUrl}/api/quests/daily/${address}`)
    if (questsResponse.ok) {
      const data = await questsResponse.json()
      setQuests(data.quests || [])
    }
  }

  const handleClaimQuest = async (questId: string) => {
    if (!address) return

    const response = await fetch(`${backendUrl}/api/quests/claim/${address}/${questId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category: 'DAILY' })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to claim quest')
    }

    // AGC: Reload quests to update UI
    const questsResponse = await fetch(`${backendUrl}/api/quests/daily/${address}`)
    if (questsResponse.ok) {
      const data = await questsResponse.json()
      setQuests(data.quests || [])
    }
  }

  // AGC: Verify All - verifies all uncompleted quests in sequence
  const handleVerifyAll = async () => {
    if (!address || verifyingAll) return
    
    // AGC: Play click sound
    playSoundSafe('/sounds/click.mp3', 0.3)
    
    const unverifiedQuests = quests.filter(q => !q.completed && !q.rewardsClaimed)
    if (unverifiedQuests.length === 0) {
      await alert('No quests to verify!', { type: 'info' })
      return
    }
    
    setVerifyingAll(true)
    setBatchProgress(`0/${unverifiedQuests.length}`)
    
    let successCount = 0
    let errorCount = 0
    
    for (let i = 0; i < unverifiedQuests.length; i++) {
      const quest = unverifiedQuests[i]
      setBatchProgress(`${i + 1}/${unverifiedQuests.length}: ${quest.name.slice(0, 20)}...`)
      
      try {
        await handleVerifyQuest(quest.id)
        successCount++
      } catch (err) {
        console.error(`Failed to verify ${quest.id}:`, err)
        errorCount++
      }
      
      // AGC: Small delay between requests to avoid rate limiting
      if (i < unverifiedQuests.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500))
      }
    }
    
    setVerifyingAll(false)
    setBatchProgress('')
    
    if (errorCount > 0) {
      await alert(`Verified ${successCount} quests, ${errorCount} failed`, { type: 'warning' })
    }
  }

  // AGC: Coin sound interval ref for claim animation
  const coinSoundIntervalRef = useRef<NodeJS.Timeout | null>(null)
  
  // AGC: Cleanup coin sound on unmount
  useEffect(() => {
    return () => {
      if (coinSoundIntervalRef.current) {
        clearInterval(coinSoundIntervalRef.current)
      }
    }
  }, [])
  
  // AGC: Calculate time until daily quest reset (midnight UTC)
  useEffect(() => {
    const updateCountdown = () => {
      // AGC: Get current UTC time
      const now = new Date()
      const utcNow = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        now.getUTCHours(),
        now.getUTCMinutes(),
        now.getUTCSeconds(),
        now.getUTCMilliseconds()
      ))
      
      // AGC: Get next midnight UTC (00:00:00 of next day)
      const nextMidnight = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() + 1, // AGC: Next day
        0, // AGC: Midnight (00:00:00)
        0,
        0,
        0
      ))
      
      const diff = nextMidnight.getTime() - utcNow.getTime()
      
      if (diff <= 0) {
        setTimeUntilReset('Resetting...')
        return
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diff % (1000 * 60)) / 1000)
      
      setTimeUntilReset(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`)
    }
    
    // AGC: Update immediately and then every second
    updateCountdown()
    const interval = setInterval(updateCountdown, 1000)
    
    return () => clearInterval(interval)
  }, [])
  
  // AGC: Claim All - uses batch endpoint to claim all in single transaction
  // AGC: FIX 2025-11-29 - No more 60s cooldown! Single transaction for all quests
  const handleClaimAll = async () => {
    if (!address || claimingAll) return
    
    // AGC: Play click sound
    playSoundSafe('/sounds/click.mp3', 0.3)
    
    const claimableQuests = quests.filter(q => q.completed && !q.rewardsClaimed)
    if (claimableQuests.length === 0) {
      await alert('No rewards to claim!', { type: 'info' })
      return
    }
    
    const totalReward = claimableQuests.reduce((sum, q) => sum + q.rewardOGB, 0)
    const confirmed = await confirm(
      `Claim ${claimableQuests.length} quest rewards?\n\n` +
      `Total: +${totalReward} OGB\n\n` +
      `This will be done in a single transaction.`,
      { title: 'Claim All Rewards' }
    )
    if (!confirmed) return
    
    setClaimingAll(true)
    setBatchProgress(`Claiming ${claimableQuests.length} quests...`)
    
    // AGC: Start playing coin sound repeatedly while claiming
    playSoundSafe('/sounds/coin.wav', 0.5)
    coinSoundIntervalRef.current = setInterval(() => {
      playSoundSafe('/sounds/coin.wav', 0.5)
    }, 800)
    
    try {
      // AGC: Use batch endpoint for single transaction
      const response = await fetch(`${backendUrl}/api/quests/batch-claim/${address}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quests: claimableQuests.map(q => ({ questId: q.id, category: 'DAILY' }))
        })
      })
      
      // AGC: Stop coin sound
      if (coinSoundIntervalRef.current) {
        clearInterval(coinSoundIntervalRef.current)
        coinSoundIntervalRef.current = null
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to batch claim')
      }
      
      const { result } = await response.json()
      
      // AGC: Play congratulations sound on success
      playSoundSafe('/sounds/congratulations.mp3', 0.6)
      
      // AGC: Update local state
      setQuests(prev => prev.map(q => 
        claimableQuests.some(cq => cq.id === q.id) 
          ? { ...q, rewardsClaimed: true } 
          : q
      ))
      
      await alert(`Claimed ${result.successCount} quests! +${result.totalOGB} OGB`, { 
        type: 'success',
        title: 'Rewards Claimed!'
      })
    } catch (err: any) {
      console.error('Batch claim failed:', err)
      
      // AGC: Stop coin sound on error
      if (coinSoundIntervalRef.current) {
        clearInterval(coinSoundIntervalRef.current)
        coinSoundIntervalRef.current = null
      }
      
      // AGC: Fall back to individual claims if batch not supported
      if (err.message?.includes('not supported') || err.message?.includes('not available')) {
        await alert('Batch claim not available. Please claim quests individually.', { type: 'warning' })
      } else {
        await alert(`Failed to claim: ${err.message}`, { type: 'error' })
      }
    } finally {
      setClaimingAll(false)
      setBatchProgress('')
    }
  }

  if (!isConnected) {
    return (
      <div className="text-center py-12 text-slate-400">
        <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-3">
          <Calendar className="w-6 h-6 text-slate-500" />
        </div>
        <p className="text-sm">Please connect your wallet to view daily quests.</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="text-center py-12 text-slate-400">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-400" />
        <p className="text-sm">Loading daily quests...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12 text-slate-400">
        <div className="w-12 h-12 rounded-full bg-red-900/30 flex items-center justify-center mx-auto mb-3">
          <Calendar className="w-6 h-6 text-red-400" />
        </div>
        <p className="text-sm text-red-400 mb-2">{error}</p>
        <p className="text-xs text-slate-500">Stake 500 OGB to unlock quests</p>
      </div>
    )
  }

  if (quests.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-3">
          <Calendar className="w-6 h-6 text-slate-500" />
        </div>
        <p className="text-sm mb-2">No daily quests available.</p>
        <p className="text-xs text-slate-500">Check back later!</p>
      </div>
    )
  }

  // AGC: Calculate counts for batch buttons
  const unverifiedCount = quests.filter(q => !q.completed && !q.rewardsClaimed).length
  const claimableCount = quests.filter(q => q.completed && !q.rewardsClaimed).length

  return (
    <div className="flex flex-col gap-2">
      {/* AGC: Daily Reset Countdown */}
      <section className="pb-2 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <p className="text-[10px] text-slate-400">Daily Reset:</p>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-emerald-400 animate-pulse" />
            <span className="text-sm font-mono font-bold text-cyan-400">{timeUntilReset || '--:--:--'}</span>
            <span className="text-[9px] text-slate-500">UTC</span>
          </div>
        </div>
      </section>
      
      {/* AGC: Progress Summary */}
      <section className="pb-3 border-b border-white/10">
        <div className="grid grid-cols-3 gap-2">
          {/* AGC: Completed */}
          <div className="bg-slate-900/60 rounded-lg p-2.5 border border-white/5">
            <p className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">Completed</p>
            <p className="text-lg font-bold text-white">{completedCount}<span className="text-slate-500 text-sm">/10</span></p>
          </div>
          
          {/* AGC: Claimed */}
          <div className="bg-slate-900/60 rounded-lg p-2.5 border border-white/5">
            <p className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">Claimed</p>
            <p className="text-lg font-bold text-emerald-400">{totalRewards}<span className="text-slate-500 text-sm"> OGB</span></p>
          </div>
          
          {/* AGC: Pending */}
          <div className="bg-slate-900/60 rounded-lg p-2.5 border border-white/5">
            <p className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">Pending</p>
            <p className="text-lg font-bold text-yellow-400">{pendingRewards}<span className="text-slate-500 text-sm"> OGB</span></p>
          </div>
        </div>
        
        {/* AGC: Progress Bar */}
        <div className="mt-3">
          <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-blue-500 via-cyan-400 to-emerald-400 transition-all duration-500"
              style={{ width: `${(completedCount / 10) * 100}%` }}
            />
          </div>
        </div>
        
        {/* AGC: Batch Action Buttons */}
        <div className="mt-3 flex gap-2">
          {/* AGC: Verify All Button */}
          <button
            onClick={handleVerifyAll}
            disabled={verifyingAll || claimingAll || unverifiedCount === 0}
            className="flex-1 px-3 py-2 text-[11px] font-semibold bg-blue-600/20 border border-blue-400/40 text-blue-300 hover:bg-blue-600/30 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
          >
            {verifyingAll ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Verifying...</span>
              </>
            ) : (
              <>
                <CheckCheck className="w-3.5 h-3.5" />
                <span>Verify All ({unverifiedCount})</span>
              </>
            )}
          </button>
          
          {/* AGC: Claim All Button */}
          <button
            onClick={handleClaimAll}
            disabled={verifyingAll || claimingAll || claimableCount === 0}
            className="flex-1 px-3 py-2 text-[11px] font-semibold bg-emerald-600/20 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-600/30 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
          >
            {claimingAll ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Claiming...</span>
              </>
            ) : (
              <>
                <Coins className="w-3.5 h-3.5" />
                <span>Claim All ({claimableCount})</span>
              </>
            )}
          </button>
        </div>
        
        {/* AGC: Batch Progress Indicator */}
        {batchProgress && (
          <div className="mt-2 text-center">
            <p className="text-[10px] text-slate-400 animate-pulse">{batchProgress}</p>
          </div>
        )}
      </section>

      {/* AGC: Quest List */}
      <section>
        {quests.map((quest) => (
          <QuestCard 
            key={quest.id} 
            quest={quest} 
            onVerify={handleVerifyQuest}
            onClaim={handleClaimQuest}
            category="DAILY"
          />
        ))}
      </section>

      {/* AGC: Modal components */}
      {Modals}
    </div>
  )
}
